import React, { useEffect, useState } from 'react';
import { splatterboxService, type DailyInsight, type BacklogItem, type PendingSource } from '../services/splatterbox.service';

const CONTENT_TYPE_LABEL: Record<string, string> = {
  technical_deepdive: 'Technical',
  product_news: 'Product',
  industry_insight: 'Industry',
  reddit_technical: 'Reddit',
};

const PLATFORM_LABEL: Record<string, string> = {
  linkedin: 'LinkedIn',
  twitter: 'X',
  reddit: 'Reddit',
};

const STATUS_COLOR: Record<string, string> = {
  queued: 'bg-yellow-500/20 text-yellow-300',
  sent_to_engineer: 'bg-blue-500/20 text-blue-300',
  approved: 'bg-green-500/20 text-green-300',
  posted: 'bg-emerald-500/20 text-emerald-300',
  rejected: 'bg-red-500/20 text-red-300',
  expired: 'bg-gray-500/20 text-gray-400',
};

function Chip({ label, color }: { label: string; color: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

function InsightItemCard({ item }: { item: DailyInsight['topItems'][0] }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block bg-[#161513] border border-white/10 rounded-xl p-4 hover:border-[#0CB4E4]/40 transition-colors group"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <span className="text-sm font-medium text-white group-hover:text-[#0CB4E4] transition-colors line-clamp-2 leading-snug">
          {item.title}
        </span>
        <Chip label={CONTENT_TYPE_LABEL[item.contentType] ?? item.contentType} color="bg-[#0CB4E4]/20 text-[#0CB4E4]" />
      </div>
      <p className="text-xs text-gray-400 line-clamp-3 mb-3">{item.summary}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">{item.sourceName}</span>
        <span className="text-xs text-gray-500">Score {item.relevanceScore}/10</span>
      </div>
    </a>
  );
}

function BacklogCard({ item }: { item: BacklogItem }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-[#1e1c1a] border border-white/10 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <span className="text-sm font-medium text-white line-clamp-2 leading-snug">{item.sourceTitle}</span>
        <div className="flex-shrink-0 flex flex-col gap-1 items-end">
          <Chip label={item.status.replace('_', ' ')} color={STATUS_COLOR[item.status] ?? 'bg-gray-500/20 text-gray-400'} />
          <Chip label={PLATFORM_LABEL[item.targetPlatform] ?? item.targetPlatform} color="bg-white/10 text-gray-300" />
        </div>
      </div>
      {item.whyThisEngineer && (
        <p className="text-xs text-[#0CB4E4]/80 mb-2 italic">"{item.whyThisEngineer}"</p>
      )}
      <button
        onClick={() => setExpanded(e => !e)}
        className="text-xs text-gray-400 hover:text-gray-200 transition-colors mb-2"
      >
        {expanded ? 'Hide draft ↑' : 'View draft ↓'}
      </button>
      {expanded && (
        <pre className="text-xs text-gray-300 bg-black/30 rounded-lg p-3 whitespace-pre-wrap font-sans border border-white/5">
          {item.draftText}
        </pre>
      )}
      <p className="text-xs text-gray-500 mt-2">{item.sourceName}</p>
    </div>
  );
}

function PendingSourceCard({ source }: { source: PendingSource }) {
  return (
    <div className="bg-[#1e1c1a] border border-yellow-500/20 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 flex-shrink-0" />
        <span className="text-sm font-medium text-white">{source.name}</span>
        <Chip label={source.type} color="bg-white/10 text-gray-300" />
      </div>
      <p className="text-xs text-gray-400 ml-3.5 mb-1 truncate">{source.query}</p>
      {source.notes && <p className="text-xs text-yellow-300/70 ml-3.5 italic">{source.notes}</p>}
    </div>
  );
}

function DigestDay({ insight, defaultOpen }: { insight: DailyInsight; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);

  const date = new Date(insight.date);
  const isToday = new Date().toDateString() === date.toDateString();
  const dateLabel = isToday
    ? 'Today'
    : date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  const runTime = insight.runCompletedAt
    ? new Date(insight.runCompletedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="border border-white/10 rounded-2xl overflow-hidden">
      {/* Day header — always visible */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 bg-[#1e1c1a] hover:bg-[#252220] transition-colors group"
      >
        <div className="flex items-center gap-3">
          <span className={`text-sm font-semibold ${isToday ? 'text-white' : 'text-gray-300'}`}>
            {dateLabel}
          </span>
          {runTime && <span className="text-xs text-gray-500">· {runTime}</span>}
          {isToday && <span className="text-xs bg-[#0CB4E4]/20 text-[#0CB4E4] px-2 py-0.5 rounded-full font-medium">Latest</span>}
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-4 text-xs text-gray-500">
            <span>{insight.sourcesChecked} sources</span>
            <span>{insight.itemsFound} items</span>
            <span>{insight.draftsGenerated} drafts</span>
          </div>
          <span className={`text-gray-500 text-sm transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>▾</span>
        </div>
      </button>

      {/* Day body */}
      {open && (
        <div className="bg-[#161513] px-5 py-5 space-y-6">
          {/* Agent summary */}
          {insight.agentSummary && (
            <div className="bg-[#1e1c1a] border border-[#0CB4E4]/20 rounded-xl p-5">
              <p className="text-xs text-[#0CB4E4] font-semibold uppercase tracking-wide mb-2">Agent Summary</p>
              <p className="text-sm text-gray-300 leading-relaxed">{insight.agentSummary}</p>
            </div>
          )}

          {/* Top items */}
          {insight.topItems.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Top Items</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {insight.topItems.map((item, i) => (
                  <InsightItemCard key={i} item={item} />
                ))}
              </div>
            </div>
          )}

          {/* Competitor activity */}
          {insight.competitorActivity.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Competitor Activity</h3>
              <div className="space-y-2">
                {insight.competitorActivity.map((c, i) => (
                  <div key={i} className="bg-[#1e1c1a] border border-white/10 rounded-xl px-4 py-3 flex items-start gap-3">
                    <span className="text-xs font-semibold text-[#0CB4E4] min-w-[80px]">{c.competitorName}</span>
                    <span className="text-xs text-gray-300 flex-1">{c.activity}</span>
                    {c.url && (
                      <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-gray-300 flex-shrink-0">↗</a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Run errors (dev visibility) */}
          {insight.runErrors?.length > 0 && (
            <div className="bg-red-900/10 border border-red-500/20 rounded-xl p-4">
              <p className="text-xs text-red-400 font-semibold uppercase tracking-wide mb-2">Run Errors</p>
              {insight.runErrors.map((e, i) => (
                <p key={i} className="text-xs text-red-300/70">{e}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SplatterboxInsightsPage() {
  const [insights, setInsights] = useState<DailyInsight[]>([]);
  const [backlog, setBacklog] = useState<BacklogItem[]>([]);
  const [pendingSources, setPendingSources] = useState<PendingSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'digest' | 'backlog'>('digest');

  useEffect(() => {
    (async () => {
      try {
        const [allData, backlogData] = await Promise.all([
          splatterboxService.getAllInsights(),
          splatterboxService.getBacklog(),
        ]);
        setInsights(allData.insights);
        setPendingSources(allData.pendingSources);
        setBacklog(backlogData.items);
      } catch {
        setError('Failed to load Splatterbox data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#161513] flex items-center justify-center">
        <div className="text-gray-400 text-sm animate-pulse">Loading digest...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#161513] flex items-center justify-center">
        <div className="text-red-400 text-sm">{error}</div>
      </div>
    );
  }

  const activeBacklog = backlog.filter(b => b.status === 'queued' || b.status === 'sent_to_engineer');

  return (
    <div className="min-h-screen bg-[#161513] px-4 py-8">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <span className="text-[#0CB4E4] text-xs font-semibold uppercase tracking-widest block mb-1">Splatterbox</span>
            <h1 className="text-2xl font-bold text-white">Daily Insights</h1>
            <p className="text-gray-400 text-sm mt-0.5">
              {insights.length} run{insights.length !== 1 ? 's' : ''} logged
            </p>
          </div>
          {pendingSources.length > 0 && (
            <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-xl px-3 py-2">
              <span className="w-2 h-2 rounded-full bg-yellow-400" />
              <span className="text-xs text-yellow-300">{pendingSources.length} source{pendingSources.length > 1 ? 's' : ''} pending review</span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[#1e1c1a] border border-white/10 rounded-xl p-1 w-fit">
          {(['digest', 'backlog'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
                activeTab === tab
                  ? 'bg-[#0CB4E4]/20 text-[#0CB4E4]'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {tab}
              {tab === 'backlog' && activeBacklog.length > 0 && (
                <span className="ml-1.5 text-xs bg-white/10 rounded-full px-1.5 py-0.5">{activeBacklog.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Digest tab — all days */}
        {activeTab === 'digest' && (
          <div className="space-y-3">
            {insights.length === 0 ? (
              <div className="bg-[#1e1c1a] border border-white/10 rounded-2xl p-8 text-center">
                <p className="text-gray-400 text-sm">No digest has run yet.</p>
                <p className="text-gray-500 text-xs mt-1">Runs daily at 07:00 UTC</p>
              </div>
            ) : (
              insights.map((insight, i) => (
                <DigestDay key={insight._id} insight={insight} defaultOpen={i === 0} />
              ))
            )}

            {/* Pending sources — shown below all days */}
            {pendingSources.length > 0 && (
              <div className="mt-6">
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Sources Discovered — Pending Review
                </h2>
                <p className="text-xs text-gray-500 mb-3">Reply "confirm source &lt;id&gt;" in the Splatterbox Slack channel to activate.</p>
                <div className="space-y-2">
                  {pendingSources.map(s => <PendingSourceCard key={s._id} source={s} />)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Backlog tab */}
        {activeTab === 'backlog' && (
          <div className="space-y-3">
            {backlog.length === 0 ? (
              <div className="bg-[#1e1c1a] border border-white/10 rounded-xl p-6 text-center">
                <p className="text-gray-400 text-sm">No items in the backlog yet.</p>
              </div>
            ) : (
              backlog.map(item => <BacklogCard key={item._id} item={item} />)
            )}
          </div>
        )}
      </div>
    </div>
  );
}
